import JSZip from "jszip";
import { useState } from "react";
import Swal from "sweetalert2";

// Téléchargement groupé des documents d'une sélection de factures.
//
// Un seul document : le PDF est enregistré tel quel. Plusieurs : ils sont
// réunis dans une archive zip fabriquée dans le navigateur.
//
// Les fichiers vivent sur Firebase Storage. Le navigateur ne peut lire leur
// contenu que parce que le bucket autorise l'origine du dashboard (règle CORS
// en lecture) : sans elle, fetch() échoue et on retombe sur l'ouverture du
// fichier dans un onglet.

export type DownloadableDoc = {
  // Absente quand la facture n'a pas (encore) de fichier : elle est ignorée
  // et signalée à la fin.
  url?: string | null;
  // Nom voulu dans l'archive, sans extension.
  name: string;
};

// Assez pour saturer la connexion sans ouvrir 50 requêtes d'un coup.
const CONCURRENCY = 4;

const SWAL_COLOR = "#02B4C0";

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Révoquer tout de suite annulerait l'enregistrement dans certains
  // navigateurs : on laisse le temps au téléchargement de démarrer.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

function extensionOf(url: string) {
  const path = decodeURIComponent(url.split("?")[0].split("#")[0]);
  const found = /\.([a-z0-9]{2,5})$/i.exec(path);
  // Les dépenses anciennes sont parfois des images, d'où l'extension lue dans
  // l'URL plutôt qu'un « .pdf » systématique.
  return found ? `.${found[1].toLowerCase()}` : ".pdf";
}

function sanitize(name: string) {
  return (
    name
      .replace(/[\\/:*?"<>|\r\n]+/g, "-")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 100) || "document"
  );
}

// Deux dépenses du même jour chez le même commerçant portent le même nom :
// sans suffixe, l'archive n'en contiendrait qu'une.
function uniqueName(taken: { [name: string]: number }, filename: string) {
  const seen = (taken[filename] ?? 0) + 1;
  taken[filename] = seen;
  if (seen === 1) return filename;
  const dot = filename.lastIndexOf(".");
  if (dot <= 0) return `${filename} (${seen})`;
  return `${filename.slice(0, dot)} (${seen})${filename.slice(dot)}`;
}

async function fetchBlob(url: string) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

function progressHtml(done: number, total: number) {
  return `${done} / ${total} document${total > 1 ? "s" : ""} récupéré${
    done > 1 ? "s" : ""
  }`;
}

function failureText(failed: string[], sansFichier: number) {
  const parts: string[] = [];
  if (failed.length) {
    const listed = failed.slice(0, 3).join(", ");
    parts.push(
      `${failed.length} document${failed.length > 1 ? "s" : ""} n'a${
        failed.length > 1 ? "nt" : ""
      } pas pu être récupéré${failed.length > 1 ? "s" : ""} (${listed}${
        failed.length > 3 ? "…" : ""
      })`
    );
  }
  if (sansFichier) {
    parts.push(
      `${sansFichier} facture${sansFichier > 1 ? "s" : ""} sans fichier`
    );
  }
  return parts.join(" — ");
}

export async function downloadDocuments(
  docs: DownloadableDoc[],
  zipBaseName: string
) {
  const ready = docs.filter((d) => !!d.url) as { url: string; name: string }[];
  const sansFichier = docs.length - ready.length;

  if (ready.length === 0) {
    await Swal.fire({
      icon: "info",
      title: "Aucun document à télécharger",
      text: "Les factures sélectionnées n'ont pas de fichier.",
      iconColor: SWAL_COLOR,
      confirmButtonColor: SWAL_COLOR,
    });
    return;
  }

  if (ready.length === 1) {
    const doc = ready[0];
    const filename = sanitize(doc.name) + extensionOf(doc.url);
    try {
      saveBlob(await fetchBlob(doc.url), filename);
    } catch (ex) {
      // Dernier recours : laisser le navigateur ouvrir le fichier.
      window.open(doc.url, "_blank");
    }
    if (sansFichier) {
      await Swal.fire({
        icon: "info",
        title: "Un seul document téléchargé",
        text: failureText([], sansFichier),
        iconColor: SWAL_COLOR,
        confirmButtonColor: SWAL_COLOR,
      });
    }
    return;
  }

  const zip = new JSZip();
  const taken: { [name: string]: number } = {};
  const failed: string[] = [];
  let done = 0;

  Swal.fire({
    title: "Préparation de l'archive",
    html: progressHtml(0, ready.length),
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    didOpen: () => Swal.showLoading(),
  });
  const showProgress = () => {
    const container = Swal.getHtmlContainer();
    if (container) container.innerHTML = progressHtml(done, ready.length);
  };

  let cursor = 0;
  const worker = async () => {
    while (cursor < ready.length) {
      const doc = ready[cursor++];
      try {
        const blob = await fetchBlob(doc.url);
        zip.file(
          uniqueName(taken, sanitize(doc.name) + extensionOf(doc.url)),
          blob
        );
      } catch (ex) {
        failed.push(doc.name);
      }
      done++;
      showProgress();
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, ready.length) }, () => worker())
  );

  const zipped = ready.length - failed.length;
  if (zipped === 0) {
    Swal.close();
    await Swal.fire({
      icon: "error",
      title: "Téléchargement impossible",
      text: "Aucun document n'a pu être récupéré.",
      confirmButtonColor: SWAL_COLOR,
    });
    return;
  }

  // Les PDF sont déjà compressés : STORE évite d'attendre un gain nul.
  const blob = await zip.generateAsync({ type: "blob", compression: "STORE" });
  Swal.close();
  saveBlob(blob, `${sanitize(zipBaseName)}.zip`);

  const problemes = failureText(failed, sansFichier);
  await Swal.fire({
    icon: problemes ? "warning" : "success",
    title: `${zipped} document${zipped > 1 ? "s" : ""} téléchargé${
      zipped > 1 ? "s" : ""
    }`,
    text: problemes || undefined,
    iconColor: SWAL_COLOR,
    confirmButtonColor: SWAL_COLOR,
    showConfirmButton: !!problemes,
    timer: problemes ? undefined : 2000,
  });
}

// Un document seul : le nom du zip ne sert pas, il n'y en a pas.
export function downloadOneDocument(
  url: string | null | undefined,
  name: string
) {
  return downloadDocuments([{ url, name }], name);
}

// Empêche un second téléchargement pendant qu'un premier tourne, et donne au
// bouton de quoi s'afficher occupé.
export function useDocumentsDownload() {
  const [isDownloading, setIsDownloading] = useState(false);

  // `run` sert quand le téléchargement est précédé d'une préparation à
  // attendre, par exemple la génération du PDF côté serveur.
  const run = async (action: () => Promise<any>) => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await action();
    } finally {
      setIsDownloading(false);
    }
  };

  const download = (docs: DownloadableDoc[], zipBaseName: string) =>
    run(() => downloadDocuments(docs, zipBaseName));

  const downloadOne = (url: string | null | undefined, name: string) =>
    run(() => downloadOneDocument(url, name));

  return { isDownloading, run, download, downloadOne };
}
